import { logError } from "../utils/log";

export type RelevantAttributes = {
  // can you define this on below
  parentOuterHTML?: string;
  innerHTML?: string;
  tagName?: string;
  id?: string;
  classList?: string[];
  [key: string]: unknown;
};

/**
 *
 * @param element the HTML element to extract relevant attributes from
 * @returns simplified object with relevant attributes
 */
export const extractRelevantAttributes = (
  element: HTMLElement
): RelevantAttributes | undefined => {
  if (!element) return undefined;

  const relevantAttributes: RelevantAttributes = {};

  try {
    // Check for most common attributes, set undefined if not present
    relevantAttributes.parentOuterHTML =
      element.parentElement?.outerHTML ?? undefined;
    relevantAttributes.innerHTML = element.innerHTML ?? undefined;
    relevantAttributes.tagName = element.tagName?.toLowerCase() ?? undefined;
    relevantAttributes.id = element.id || undefined;

    // Check for classes, default to undefined if no classes are present
    relevantAttributes.classList =
      element.classList.length > 0 ? Array.from(element.classList) : undefined;

    // Check for other attributes like name, role, etc., set undefined if not present
    const attributeList = [
      "name",
      "role",
      "type",
      "placeholder",
      "value",
      "href",
      "src",
      "alt",
    ];
    attributeList.forEach((attr) => {
      const attrValue = element.getAttribute(attr);
      relevantAttributes[attr] = attrValue ?? undefined;
    });

    // Include data-* attributes, default to undefined if not present
    const dataAttributes = Array.from(element.attributes).filter((attr) =>
      attr.name.startsWith("data-")
    );
    dataAttributes.forEach((attr) => {
      relevantAttributes[attr.name] = attr.value ?? undefined;
    });

    // Include any custom attributes, default to undefined if not present
    const customAttributes = Array.from(element.attributes).filter(
      (attr) =>
        !attributeList.includes(attr.name) && !attr.name.startsWith("data-")
    );
    customAttributes.forEach((attr) => {
      relevantAttributes[attr.name] = attr.value ?? undefined;
    });
  } catch (error) {
    logError("Error extracting relevant attributes ", error);
  }

  return relevantAttributes;
};
